import React from 'react';
import styled from 'styled-components';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

const Container = styled.div`
  min-height: calc(100vh - var(--header-height));
  padding: 2rem 1rem;
  max-width: 800px;
  margin: 0 auto;
  margin-top: var(--header-height);

  @media (min-width: 768px) {
    padding: 3rem 2rem;
  }
`;

const Title = styled.h1`
  font-size: 2.5rem;
  margin-bottom: 1rem;
  color: var(--text-color);
`;

const LastUpdated = styled.p`
  color: var(--gray-dark);
  margin-bottom: 2rem;
  font-style: italic;
`;

const Section = styled.section`
  margin-bottom: 2rem;

  h2 {
    font-size: 1.5rem;
    margin-bottom: 1rem;
    color: var(--text-color);
  }

  h3 {
    font-size: 1.2rem;
    margin-bottom: 0.75rem;
    color: var(--text-color);
  }

  p {
    line-height: 1.8;
    color: var(--gray-dark);
    margin-bottom: 1rem;
  }

  ul {
    margin-left: 2rem;
    margin-bottom: 1rem;
    line-height: 1.8;
    color: var(--gray-dark);
  }

  li {
    margin-bottom: 0.5rem;
  }

  a {
    color: var(--primary-color);
    text-decoration: none;

    &:hover {
      text-decoration: underline;
    }
  }
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  margin: 1rem 0;
  font-size: 0.9rem;

  th, td {
    padding: 0.75rem;
    text-align: left;
    border: 1px solid var(--gray-light);
  }

  th {
    background-color: var(--gray-light);
    font-weight: 600;
    color: var(--text-color);
  }

  td {
    color: var(--gray-dark);
  }

  @media (max-width: 768px) {
    font-size: 0.8rem;

    th, td {
      padding: 0.5rem;
    }
  }
`;

const ContactBox = styled.div`
  background-color: var(--gray-light);
  padding: 1.5rem;
  border-radius: 8px;
  margin-top: 2rem;

  h3 {
    margin-bottom: 1rem;
    color: var(--text-color);
  }

  p {
    margin-bottom: 0.5rem;
    line-height: 1.6;
  }
`;

const CookiePolicy: React.FC = () => {
  const { t } = useTranslation();

  return (
    <Container>
      <Title>{t('cookiePolicy.title')}</Title>

      <Section>
        <p>{t('cookiePolicy.intro')}</p>
      </Section>

      <Section>
        <h2>{t('cookiePolicy.section1.heading')}</h2>
        <p>{t('cookiePolicy.section1.text1')}</p>
        <p>{t('cookiePolicy.section1.text2')}</p>
      </Section>

      <Section>
        <h2>{t('cookiePolicy.section2.heading')}</h2>
        <p>{t('cookiePolicy.section2.intro')}</p>
        <ul>
          <li><strong>{t('cookiePolicy.section2.essentialLabel')}</strong> {t('cookiePolicy.section2.essentialText')}</li>
          <li><strong>{t('cookiePolicy.section2.performanceLabel')}</strong> {t('cookiePolicy.section2.performanceText')}</li>
          <li><strong>{t('cookiePolicy.section2.functionalLabel')}</strong> {t('cookiePolicy.section2.functionalText')}</li>
          <li><strong>{t('cookiePolicy.section2.targetingLabel')}</strong> {t('cookiePolicy.section2.targetingText')}</li>
        </ul>
      </Section>

      <Section>
        <h2>{t('cookiePolicy.section3.heading')}</h2>
        <Table>
          <thead>
            <tr>
              <th>{t('cookiePolicy.section3.typeHeader')}</th>
              <th>{t('cookiePolicy.section3.purposeHeader')}</th>
              <th>{t('cookiePolicy.section3.durationHeader')}</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>{t('cookiePolicy.section3.row1Type')}</td>
              <td>{t('cookiePolicy.section3.row1Purpose')}</td>
              <td>{t('cookiePolicy.section3.row1Duration')}</td>
            </tr>
            <tr>
              <td>{t('cookiePolicy.section3.row2Type')}</td>
              <td>{t('cookiePolicy.section3.row2Purpose')}</td>
              <td>{t('cookiePolicy.section3.row2Duration')}</td>
            </tr>
            <tr>
              <td>{t('cookiePolicy.section3.row3Type')}</td>
              <td>{t('cookiePolicy.section3.row3Purpose')}</td>
              <td>{t('cookiePolicy.section3.row3Duration')}</td>
            </tr>
            <tr>
              <td>{t('cookiePolicy.section3.row4Type')}</td>
              <td>{t('cookiePolicy.section3.row4Purpose')}</td>
              <td>{t('cookiePolicy.section3.row4Duration')}</td>
            </tr>
            <tr>
              <td>{t('cookiePolicy.section3.row5Type')}</td>
              <td>{t('cookiePolicy.section3.row5Purpose')}</td>
              <td>{t('cookiePolicy.section3.row5Duration')}</td>
            </tr>
          </tbody>
        </Table>
      </Section>

      <Section>
        <h2>{t('cookiePolicy.section4.heading')}</h2>
        <p>{t('cookiePolicy.section4.text')}</p>
        <Table>
          <thead>
            <tr>
              <th>{t('cookiePolicy.section4.nameHeader')}</th>
              <th>{t('cookiePolicy.section4.purposeHeader')}</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>{t('cookiePolicy.section4.row1Name')}</td>
              <td>{t('cookiePolicy.section4.row1Purpose')}</td>
            </tr>
            <tr>
              <td>{t('cookiePolicy.section4.row2Name')}</td>
              <td>{t('cookiePolicy.section4.row2Purpose')}</td>
            </tr>
            <tr>
              <td>{t('cookiePolicy.section4.row3Name')}</td>
              <td>{t('cookiePolicy.section4.row3Purpose')}</td>
            </tr>
            <tr>
              <td>{t('cookiePolicy.section4.row4Name')}</td>
              <td>{t('cookiePolicy.section4.row4Purpose')}</td>
            </tr>
          </tbody>
        </Table>
      </Section>

      <Section>
        <h2>{t('cookiePolicy.section5.heading')}</h2>
        <p>{t('cookiePolicy.section5.text1')}</p>
        <p>
          {t('cookiePolicy.section5.text2Pre')}<a href="https://tools.google.com/dlpage/gaoptout" target="_blank" rel="noopener noreferrer">{t('cookiePolicy.section5.text2LinkText')}</a>.
        </p>
      </Section>

      <Section>
        <h2>{t('cookiePolicy.section6.heading')}</h2>
        <p>{t('cookiePolicy.section6.text')}</p>
      </Section>

      <Section>
        <h2>{t('cookiePolicy.section7.heading')}</h2>
        <p>{t('cookiePolicy.section7.text1')}</p>
        <p>{t('cookiePolicy.section7.text2')}</p>
      </Section>

      <Section>
        <h2>{t('cookiePolicy.section8.heading')}</h2>
        <p>{t('cookiePolicy.section8.intro')}</p>
        <ul>
          <li><strong>{t('cookiePolicy.section8.bannerLabel')}</strong> {t('cookiePolicy.section8.bannerText')}</li>
          <li><strong>{t('cookiePolicy.section8.browserSettingsLabel')}</strong> {t('cookiePolicy.section8.browserSettingsText')}</li>
          <li><strong>{t('cookiePolicy.section8.optingOutLabel')}</strong> {t('cookiePolicy.section8.optingOutText')}</li>
        </ul>

        <h3>{t('cookiePolicy.section8.browserControlHeading')}</h3>
        <ul>
          <li><a href="https://support.google.com/chrome/answer/95647" target="_blank" rel="noopener noreferrer">{t('cookiePolicy.section8.chromeLinkText')}</a></li>
          <li><a href="https://support.mozilla.org/en-US/kb/enable-and-disable-cookies-website-preferences" target="_blank" rel="noopener noreferrer">{t('cookiePolicy.section8.firefoxLinkText')}</a></li>
          <li><a href="https://support.apple.com/en-gb/guide/safari/sfri11471/mac" target="_blank" rel="noopener noreferrer">{t('cookiePolicy.section8.safariLinkText')}</a></li>
          <li><a href="https://support.microsoft.com/en-us/help/4027947" target="_blank" rel="noopener noreferrer">{t('cookiePolicy.section8.edgeLinkText')}</a></li>
        </ul>
      </Section>

      <Section>
        <h2>{t('cookiePolicy.section9.heading')}</h2>
        <p>{t('cookiePolicy.section9.text')}</p>
      </Section>

      <Section>
        <h2>{t('cookiePolicy.section10.heading')}</h2>
        <p>{t('cookiePolicy.section10.text')}</p>
      </Section>

      <Section>
        <h2>{t('cookiePolicy.section11.heading')}</h2>
        <p>
          {t('cookiePolicy.section11.text1Pre')}<a href="https://www.aboutcookies.org" target="_blank" rel="noopener noreferrer">{t('cookiePolicy.section11.text1Link1Text')}</a>{t('cookiePolicy.section11.text1Middle')}<a href="https://www.allaboutcookies.org" target="_blank" rel="noopener noreferrer">{t('cookiePolicy.section11.text1Link2Text')}</a>.
        </p>
        <p>
          {t('cookiePolicy.section11.text2Pre')}<a href="https://www.youronlinechoices.com" target="_blank" rel="noopener noreferrer">{t('cookiePolicy.section11.text2LinkText')}</a>.
        </p>
      </Section>

      <Section>
        <h2>{t('cookiePolicy.section12.heading')}</h2>
        <p>
          {t('cookiePolicy.section12.textPre')}<Link to="/privacy-policy">{t('cookiePolicy.section12.linkText')}</Link>{t('cookiePolicy.section12.textPost')}
        </p>
      </Section>

      <ContactBox>
        <h3>{t('cookiePolicy.contactBox.heading')}</h3>
        <p><strong>{t('cookiePolicy.contactBox.companyName')}</strong></p>
        <p>{t('cookiePolicy.contactBox.address')}</p>
        <p>{t('cookiePolicy.contactBox.email')}</p>
        <p>{t('cookiePolicy.contactBox.phone')}</p>
      </ContactBox>
    </Container>
  );
};

export default CookiePolicy;
